import {
  Box,
  Button,
  HStack,
  Popover,
  Portal,
  Switch,
  Text,
  VStack
} from "@chakra-ui/react"
import {
  AugmentationPopover,
  useAugmentationDialog
} from "@pathwright/ux/src/components/augmentation/AugmentationDialog"
import {
  AUGMENTATION_PANEL_SWITCH_CONTROL_PROPS,
  AUGMENTATION_PANEL_TYPOGRAPHY_CSS
} from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import ColorPicker from "@pathwright/ux/src/components/color/ColorPicker"
import MediaManager from "@pathwright/features/src/modules/media-manager/MediaManager"
import { useState } from "react"
import { useCoverPageTranslate } from "../utils"

type CoverPageBackgroundSettingsPopoverProps = {
  contextKey: string
  disabled?: boolean
  isEditable?: boolean
  backgroundOverlay: string
  savedBackgroundOverlay: string
  isAmbient: boolean
  onChooseBackgroundImage: (url: string) => void
  onChangeBackgroundOverlay: (overlay: string) => void
  onChangeAmbient: (isAmbient: boolean) => void
  children: React.ReactNode
}

function BackgroundImagePickerButton({
  contextKey,
  disabled,
  onChoose
}: {
  contextKey: string
  disabled?: boolean
  onChoose: (url: string) => void
}) {
  const { t } = useCoverPageTranslate()
  const [mediaManagerOpen, setMediaManagerOpen] = useState(false)
  useAugmentationDialog(mediaManagerOpen)

  return (
    <MediaManager
      contextKey={contextKey}
      mediaType="image"
      pickOptions={{ maxFiles: 1 }}
      onOpenChange={setMediaManagerOpen}
      onChooseMedia={(media: { url: string }) => onChoose(media.url)}
      renderLoading={() => null}
      renderButton={({
        onClick
      }: {
        onClick?: React.MouseEventHandler<HTMLElement>
      }) => (
        <Button w="full" onClick={onClick} disabled={disabled}>
          <Pathicon icon="image" />
          {t("panel_header.replace_background_image", {
            defaultValue: "Replace Background Image"
          })}
        </Button>
      )}
    />
  )
}

export function CoverPageBackgroundSettingsPopover({
  contextKey,
  disabled,
  isEditable = true,
  backgroundOverlay,
  savedBackgroundOverlay,
  isAmbient,
  onChooseBackgroundImage,
  onChangeBackgroundOverlay,
  onChangeAmbient,
  children
}: CoverPageBackgroundSettingsPopoverProps) {
  const { t } = useCoverPageTranslate()
  return (
    <AugmentationPopover positioning={{ placement: "bottom-end" }} lazyMount>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            css={AUGMENTATION_PANEL_TYPOGRAPHY_CSS}
            w="340px"
            zIndex="popover"
          >
            <Popover.Arrow />
            <Popover.Body p={4}>
              <VStack align="stretch" gap={5}>
                <BackgroundImagePickerButton
                  contextKey={contextKey}
                  disabled={disabled || !isEditable}
                  onChoose={onChooseBackgroundImage}
                />
                <HStack align="center" justify="space-between" gap={4}>
                  <VStack align="start" gap={1} flex="1">
                    <Text fontSize="sm" fontWeight="semibold" m={0}>
                      {t("panel_header.adjust_dimming", {
                        defaultValue: "Adjust dimming"
                      })}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="fg.muted"
                      lineHeight="1.35"
                      m={0}
                    >
                      {t("panel_header.dimming_hint", {
                        defaultValue:
                          "A subtle overlay on the image ensures text is easy to read."
                      })}
                    </Text>
                  </VStack>
                  <ColorPicker
                    disabled={disabled || !isEditable}
                    color={backgroundOverlay}
                    initialColor={savedBackgroundOverlay}
                    onChange={onChangeBackgroundOverlay}
                    recent
                  />
                </HStack>
                <HStack align="center" justify="space-between" gap={4}>
                  <VStack align="start" gap={1} flex="1">
                    <Text fontSize="sm" fontWeight="semibold" m={0}>
                      {t("panel_header.ambient", { defaultValue: "Ambient" })}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="fg.muted"
                      lineHeight="1.35"
                      m={0}
                    >
                      {t("panel_header.ambient_hint", {
                        defaultValue:
                          "Blur the background image to create an atmospheric effect."
                      })}
                    </Text>
                  </VStack>
                  <Switch.Root
                    checked={isAmbient}
                    disabled={disabled || !isEditable}
                    onCheckedChange={({ checked }) =>
                      onChangeAmbient(checked === true)
                    }
                  >
                    <Switch.HiddenInput
                      aria-label={t("panel_header.ambient", {
                        defaultValue: "Ambient"
                      })}
                    />
                    <Switch.Control
                      {...AUGMENTATION_PANEL_SWITCH_CONTROL_PROPS}
                    >
                      <Switch.Thumb />
                    </Switch.Control>
                  </Switch.Root>
                </HStack>
              </VStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </AugmentationPopover>
  )
}
